/**
 * @file Client-side error reporting (F1.9, 1.13.6).
 *
 * A copy of ecommerce-platform's `src/lib/observability/reporter.ts`, with
 * only this header changed: the bounds, the transport and the fixed path are
 * the storefront's, kept in step by hand (CLAUDE.md section 5).
 *
 * The browser half of structured logging and observability. The API already
 * binds an X-Request-Id to every log line and echoes it back; this module
 * closes the loop from the other end, so a failure a customer saw can be
 * found in the API and worker logs for the same action.
 *
 * Destination: no third-party error provider is chosen, and one is not being
 * introduced here. Reports go to a fixed path on our own API
 * (`CLIENT_ERROR_PATH` under `apiBaseUrl`) and nowhere else. There is no
 * configurable URL on purpose: a report can never be aimed at a vendor host.
 * `ecommerce-api` accepts it (F1.9) and redacts every report again; sending
 * is still off unless `errorReportingEnabled` is set in `/config.json`, and
 * until then the report is written to the console as one structured line.
 *
 * The one hand-written fetch in this app (`client.ts` forbids them for known
 * endpoints): a report needs `keepalive` and `credentials: 'omit'`, which the
 * typed client, built to send the session cookie, does not give it.
 *
 * Bounds, because an error reporter that misbehaves is worse than none:
 *
 * - identical reports are sent once (`reportKey` de-duplication), so a render
 *   loop produces one report, not one per frame;
 * - at most MAX_REPORTS_PER_WINDOW in a RATE_WINDOW_MS window and
 *   MAX_REPORTS_PER_PAGE for the life of the page;
 * - the transport never retries, and a failed send disables sending for the
 *   rest of the page, so a failing report cannot feed itself;
 * - `sending` guards re-entry, so an error raised inside the transport cannot
 *   start another report.
 *
 * Privacy: the payload is built by `errorReport.ts`, which drops query
 * strings, reduces the path to a route pattern and redacts value-shaped text.
 * No form values, no storage contents, no cookies, no full URLs.
 */
import { getLastRequestId, newRequestId, REQUEST_ID_HEADER } from '../../api/client';

import { buildClientErrorReport, reportKey } from './errorReport';

import type { ClientErrorInput, ClientErrorReport } from './errorReport';
import type { RuntimeConfig } from '../config/schema';

/** Path on our own API that accepts a report. Fixed, never configurable. */
export const CLIENT_ERROR_PATH = '/v1/client-errors';

/** Distinct reports sent in one rate window. */
export const MAX_REPORTS_PER_WINDOW = 5;

/** Length of the rate window in milliseconds. */
export const RATE_WINDOW_MS = 60_000;

/** Distinct reports sent for the life of the page. */
export const MAX_REPORTS_PER_PAGE = 20;

/** How many de-duplication keys are remembered before the oldest is dropped. */
export const MAX_TRACKED_KEYS = 50;

/** Delivers one report. Injected so tests never touch the network. */
export type SendReport = (endpoint: string, report: ClientErrorReport) => Promise<void>;

/** Writes one report where an operator can read it. */
export type LogReport = (report: ClientErrorReport) => void;

/** Options for createErrorReporter. */
export interface ErrorReporterOptions {
  /** Absolute or relative endpoint URL, or null to log without sending. */
  readonly endpoint: string | null;
  /** Transport. Defaults to a keepalive fetch with no credentials. */
  readonly send?: SendReport;
  /** Operator sink. Defaults to one console.error line. */
  readonly log?: LogReport;
  /** Clock, injectable for the rate window test. */
  readonly now?: () => number;
}

/** What a reporter exposes. */
export interface ErrorReporter {
  /** Reports one failure. Never throws and never returns a promise to await. */
  report: (input: ClientErrorInput) => void;
  /** Attaches the global error and rejection handlers; returns the detach. */
  install: () => () => void;
}

/**
 * Joins the API base (a path such as "/api" or an absolute origin) with the
 * report path without depending on the URL constructor resolving a relative
 * base, which it does not.
 */
export function clientErrorEndpoint(config: Pick<RuntimeConfig, 'apiBaseUrl'>): string {
  return `${config.apiBaseUrl.replace(/\/+$/, '')}${CLIENT_ERROR_PATH}`;
}

/**
 * Default transport. `keepalive` so a report survives the navigation that
 * often follows an error, `credentials: 'omit'` so the session cookie is
 * not attached: this is a bug report, not an identified event.
 */
const defaultSend: SendReport = async (endpoint, report) => {
  const response = await fetch(endpoint, {
    method: 'POST',
    keepalive: true,
    credentials: 'omit',
    headers: {
      'content-type': 'application/json',
      [REQUEST_ID_HEADER]: newRequestId(),
    },
    body: JSON.stringify(report),
  });
  if (!response.ok) {
    throw new Error(`client error report rejected with ${String(response.status)}`);
  }
};

/** Default operator sink: one structured line, never shown to the customer. */
const defaultLog: LogReport = (report) => {
  console.error('client error', JSON.stringify(report));
};

/** Creates a bounded reporter. One per page; `initErrorReporting` owns the instance. */
export function createErrorReporter(options: ErrorReporterOptions): ErrorReporter {
  const { endpoint, send = defaultSend, log = defaultLog, now = () => Date.now() } = options;

  const seen = new Set<string>();
  let windowStart = now();
  let inWindow = 0;
  let total = 0;
  let sendingDisabled = endpoint === null;
  // Re-entry guard: an error thrown while a report is being built or sent
  // must not start a second report.
  let busy = false;

  /** True when this report is within both the window and the page budget. */
  function withinBudget(): boolean {
    const timestamp = now();
    if (timestamp - windowStart >= RATE_WINDOW_MS) {
      windowStart = timestamp;
      inWindow = 0;
    }
    return inWindow < MAX_REPORTS_PER_WINDOW && total < MAX_REPORTS_PER_PAGE;
  }

  function report(input: ClientErrorInput): void {
    if (busy) return;
    busy = true;
    try {
      const built = buildClientErrorReport(input, getLastRequestId());
      const key = reportKey(built);
      if (seen.has(key)) return;
      if (!withinBudget()) return;
      if (seen.size >= MAX_TRACKED_KEYS) {
        const oldest = seen.values().next().value;
        if (oldest !== undefined) seen.delete(oldest);
      }
      seen.add(key);
      inWindow += 1;
      total += 1;
      log(built);
      if (endpoint === null || sendingDisabled) return;
      // Fire and forget. A failure disables sending rather than retrying, so
      // an unreachable or missing endpoint costs at most one request.
      void send(endpoint, built).catch(() => {
        sendingDisabled = true;
      });
    } catch {
      // A reporter that throws would replace the original failure with its
      // own. Swallowing is the whole point of this catch.
    } finally {
      busy = false;
    }
  }

  function install(): () => void {
    const onError = (event: ErrorEvent): void => {
      report({
        kind: 'uncaught_error',
        error: event.error ?? event.message,
        pathname: window.location.pathname,
      });
    };
    const onRejection = (event: PromiseRejectionEvent): void => {
      report({
        kind: 'unhandled_rejection',
        error: event.reason,
        pathname: window.location.pathname,
      });
    };
    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onRejection);
    return () => {
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onRejection);
    };
  }

  return { report, install };
}

/**
 * The page's reporter. A module singleton because the global handlers and the
 * route error element both need it, and neither can reach a React context:
 * one runs outside React, the other runs after the tree has already failed.
 */
let current: ErrorReporter | null = null;

/**
 * Creates the reporter from runtime configuration and attaches the global
 * handlers. Returns the detach function. Safe to call once per page.
 */
export function initErrorReporting(
  config: Pick<RuntimeConfig, 'apiBaseUrl' | 'errorReportingEnabled'>,
  options: Omit<ErrorReporterOptions, 'endpoint'> = {},
): () => void {
  const endpoint = config.errorReportingEnabled ? clientErrorEndpoint(config) : null;
  const reporter = createErrorReporter({ ...options, endpoint });
  current = reporter;
  const detach = reporter.install();
  return () => {
    detach();
    if (current === reporter) current = null;
  };
}

/** Reports a failure through the page's reporter. A no-op before init. */
export function reportClientError(input: ClientErrorInput): void {
  current?.report(input);
}

/** Drops the page reporter. Tests only. */
export function resetErrorReportingForTests(): void {
  current = null;
}
